import React, { useMemo } from "react";
import { Formik, Form, Field } from "formik";
import renderIf from "render-if";
import styled from "styled-components";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import * as Yup from "yup";

import withFormikField from "../hoc/withFormikField";
import { CallToActionButton } from "./Buttons";
import PhoneTextField from "./PhoneTextField";
import HumanVerification from "../containers/HumanVerification";
import DisclaimerField from "./DisclaimerField";

const FormStyled = styled(Form)`
  display: flex;
  flex-direction: column;
`;

const SubmitButton = styled(CallToActionButton)`
  && {
    margin: 20px 0;
  }
`;

const StyledTextField = styled(TextField).attrs({
  variant: "outlined",
})`
  && {
    margin-bottom: 20px;
  }
`;

const TextFieldFormik = withFormikField(StyledTextField);
const PhoneFieldFormik = withFormikField(PhoneTextField);
const SelectFormik = withFormikField(Select);

const useContactSchema = ({ required, humanVerification, preferredMethod }) =>
  useMemo(() => {
    let contact = {
      firstName: Yup.string(),
      lastName: Yup.string(),
      postalCode: Yup.string(),
      email: Yup.string().email("Invalid email"),
      phone: Yup.string(),
      disclaimer: Yup.bool(),
    };

    if (required) {
      contact.firstName = contact.firstName.required("First name required");
      contact.lastName = contact.lastName.required("Last name required");
      contact.postalCode = contact.postalCode.required("Zip required");
      contact.email = contact.email.required("Email required");
      if (preferredMethod) {
        contact.phone = contact.phone.test(
          "Required if phone selected",
          "Phone number required",
          function (value) {
            const { preferredMethodOfContact } = this.parent;

            if (preferredMethodOfContact === "phone") {
              return !!value;
            }
            return true;
          }
        );
      } else {
        contact.phone = contact.phone.required("Phone number required");
      }
    }

    if (humanVerification) {
      contact.verification = Yup.object({
        valid: Yup.bool().oneOf([true]),
        code: Yup.string(),
      });
    }
    return Yup.object().shape(contact);
  }, [required, humanVerification, preferredMethod]);

const ContactForm = ({
  onSubmit,
  disabled,
  required,
  disclaimer,
  actionName,
  humanVerification,
  message,
  preferredMethod,
  ...props
}) => {
  const contactSchema = useContactSchema({
    required,
    humanVerification,
    preferredMethod,
  });

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={{
        firstName: "",
        lastName: "",
        postalCode: "",
        email: "",
        phone: "",
        verification: false,
        disclaimer: false,
        preferredMethodOfContact: preferredMethod ? "email" : undefined,
      }}
      validationSchema={contactSchema}
      render={({ isValid, values }) => {
        const { preferredMethodOfContact } = values;
        return (
          <FormStyled {...props}>
            <Field
              id="first-name-input"
              component={TextFieldFormik}
              name="firstName"
              label={`First Name ${required ? "*" : ""}`}
              className="fs-exclude"
            />
            <Field
              id="last-name-input"
              component={TextFieldFormik}
              name="lastName"
              label={`Last Name ${required ? "*" : ""}`}
              className="fs-exclude"
            />
            <Field
              id="postal-code-input"
              component={TextFieldFormik}
              name="postalCode"
              label={`Zip / Postal Code ${required ? "*" : ""}`}
              className="fs-exclude"
            />
            <Field
              id="email-input"
              component={TextFieldFormik}
              name="email"
              label={`Email ${required ? "*" : ""}`}
              type="email"
              className="fs-exclude"
            />
            <Field
              component={PhoneFieldFormik}
              id="phone-input"
              name="phone"
              label={`Phone ${
                preferredMethodOfContact === "phone" ||
                preferredMethodOfContact === undefined
                  ? "*"
                  : ""
              }`}
              type="tel"
              className="fs-exclude"
              variant="outlined"
            />
            {renderIf(preferredMethod)(
              <FormControl style={{ marginTop: 35 }}>
                <InputLabel style={{ top: -20 }}>
                  Preferred Method of Contact
                </InputLabel>
                <Field
                  name="preferredMethodOfContact"
                  children={[
                    <MenuItem value="email">Email</MenuItem>,
                    <MenuItem value="phone">Phone</MenuItem>,
                  ]}
                  component={SelectFormik}
                  variant="outlined"
                  style={{ width: "50%" }}
                />
              </FormControl>
            )}
            {renderIf(message)(
              <Field
                style={{ marginTop: 20 }}
                id="message-input"
                component={TextFieldFormik}
                name="message"
                label="Additional Comments"
                rows="2"
                multiline
                className="fs-exclude"
              />
            )}
            {renderIf(humanVerification)(
              <Field component={HumanVerification} name="verification" />
            )}
            {renderIf(disclaimer)(
              <DisclaimerField id="form-disclaimer" name="disclaimer">
                {disclaimer}
              </DisclaimerField>
            )}
            <SubmitButton
              type="submit"
              disabled={disabled || (required && !isValid)}
            >
              {actionName}
            </SubmitButton>
            {renderIf(required)(
              <Typography variant="subtitle2" color="textSecondary">
                *Required
              </Typography>
            )}
          </FormStyled>
        );
      }}
    ></Formik>
  );
};

export default ContactForm;
